import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import { AlertTriangle, Activity, CheckCircle2, Shield } from 'lucide-react';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import AuditAlertModal from './AuditAlertModal';
import '../styles/InventoryEquipment.css';
import '../styles/InventoryAudit.css';

const FIND = `
  query FindAllResourceAudits {
    findAllResourceAudits {
      id
      alertNumber
      detectedAt
      alertType
      productAsset
      site
      expectedValue
      actualValue
      variancePercent
      severity
      status
      activity
    }
  }
`;

const UPDATE = `
  mutation UpdateResourceAudit($updateResourceAuditInput: UpdateResourceAuditInput!) {
    updateResourceAudit(updateResourceAuditInput: $updateResourceAuditInput) {
      id
      status
      activity
    }
  }
`;

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function typeLabel(type) {
  const map = {
    ExcessUsage: 'Excess Usage',
    MissingEquipment: 'Missing Equipment',
    MissingInventory: 'Missing Inventory',
    TheftRisk: 'Theft Risk',
    LowProductivity: 'Low Productivity',
  };
  return map[type] || type || '—';
}

function typeClass(type) {
  const map = {
    ExcessUsage: 'iaud-chip--yellow',
    MissingEquipment: 'iaud-chip--blue',
    MissingInventory: 'iaud-chip--gray',
    TheftRisk: 'iaud-chip--red',
    LowProductivity: 'iaud-chip--gray',
  };
  return map[type] || 'iaud-chip--gray';
}

function severityClass(s) {
  if (s === 'Critical') return 'iaud-chip--red';
  if (s === 'High') return 'iaud-chip--orange';
  return 'iaud-chip--yellow';
}

function statusClass(s) {
  if (s === 'Open') return 'iaud-chip--red';
  if (s === 'Investigating') return 'iaud-chip--yellow';
  if (s === 'Resolved') return 'iaud-chip--green';
  return 'iaud-chip--gray';
}

function formatVariance(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v}%`;
}

function varianceTone(n) {
  if (n == null || n === '') return '';
  const v = Math.abs(Number(n));
  if (v >= 80) return 'iaud-var--critical';
  if (v >= 40) return 'iaud-var--high';
  if (v >= 20) return 'iaud-var--medium';
  return '';
}

function isThisMonth(raw) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export default function AuditsDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const overlay = useAdminListOverlay(rows);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await graphqlRequest(FIND);
      setRows(
        Array.isArray(data?.findAllResourceAudits)
          ? data.findAllResourceAudits
          : [],
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to load audit alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    const open = rows.filter((r) => r.status === 'Open').length;
    const investigating = rows.filter((r) => r.status === 'Investigating').length;
    const resolvedMonth = rows.filter(
      (r) => r.status === 'Resolved' && isThisMonth(r.detectedAt),
    ).length;
    const dismissed = rows.filter((r) => r.status === 'Dismissed').length;
    return { open, investigating, resolvedMonth, dismissed };
  }, [rows]);

  const setStatus = async (alert, status) => {
    if (status === 'Investigating' && String(alert.status) === 'Investigating') {
      toast.info('This alert is already being investigated');
      return;
    }
    setSaving(true);
    try {
      const data = await graphqlRequest(UPDATE, {
        updateResourceAuditInput: { id: alert.id, status: String(status) },
      });
      const updated = data?.updateResourceAudit;
      setRows((prev) =>
        prev.map((r) =>
          r.id === alert.id
            ? { ...r, status, activity: updated?.activity ?? r.activity }
            : r,
        ),
      );
      toast.success(
        status === 'Resolved'
          ? 'Alert marked as resolved'
          : status === 'Dismissed'
            ? 'Alert dismissed'
            : 'Alert escalated',
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to update alert');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="iaud-page">
      <header className="iaud-header">
        <h1>Resource Audit Engine</h1>
      </header>

      <section className="ieq-stats">
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--red" aria-hidden>
            <AlertTriangle size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">OPEN ALERTS</div>
            <div className="ieq-stat-value">{stats.open}</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--yellow" aria-hidden>
            <Activity size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">INVESTIGATING</div>
            <div className="ieq-stat-value">{stats.investigating}</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--green" aria-hidden>
            <CheckCircle2 size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">RESOLVED THIS MONTH</div>
            <div className="ieq-stat-value">{stats.resolvedMonth}</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--gray" aria-hidden>
            <Shield size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">DISMISSED</div>
            <div className="ieq-stat-value">{stats.dismissed}</div>
          </div>
        </article>
      </section>

      <div className="iaud-table-wrap">
        {loading ? (
          <div className="iaud-empty">Scanning resources for discrepancies…</div>
        ) : rows.length === 0 ? (
          <div className="iaud-empty">
            No audit alerts. Excess usage, low stock, and missing equipment will
            appear here automatically.
          </div>
        ) : (
          <table className="iaud-table">
            <thead>
              <tr>
                <th>Alert ID</th>
                <th>Date</th>
                <th>Type</th>
                <th>Product / Asset</th>
                <th>Site</th>
                <th>Expected</th>
                <th>Actual</th>
                <th>Variance</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const hot =
                  row.status === 'Open' || row.severity === 'Critical';
                return (
                  <tr
                    key={row.id}
                    className={hot ? 'iaud-row--hot' : ''}
                  >
                    <td>
                      <button
                        type="button"
                        className="iaud-id"
                        onClick={() => overlay.openView(row)}
                      >
                        {row.alertNumber}
                      </button>
                    </td>
                    <td>{formatDate(row.detectedAt)}</td>
                    <td>
                      <span className={`iaud-chip ${typeClass(row.alertType)}`}>
                        {typeLabel(row.alertType)}
                      </span>
                    </td>
                    <td>{row.productAsset || '—'}</td>
                    <td>{row.site || '—'}</td>
                    <td>{row.expectedValue || '—'}</td>
                    <td>{row.actualValue || '—'}</td>
                    <td className={varianceTone(row.variancePercent)}>
                      {formatVariance(row.variancePercent)}
                    </td>
                    <td>
                      <span className={`iaud-chip ${severityClass(row.severity)}`}>
                        {row.severity}
                      </span>
                    </td>
                    <td>
                      <span className={`iaud-chip ${statusClass(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className="iaud-actions">
                        <button
                          type="button"
                          className="ieq-icon-btn"
                          title="View alert"
                          onClick={() => overlay.openView(row)}
                        >
                          <FaEye />
                        </button>
                        {row.status === 'Open' && (
                          <button
                            type="button"
                            className="ieq-btn"
                            disabled={saving}
                            onClick={() => setStatus(row, 'Investigating')}
                          >
                            Escalate
                          </button>
                        )}
                        {row.status === 'Open' && (
                          <button
                            type="button"
                            className="iaud-btn-resolve"
                            disabled={saving}
                            onClick={() => setStatus(row, 'Resolved')}
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && rows.length > 0 && (
          <div className="iaud-foot">
            Showing {rows.length} audit record{rows.length === 1 ? '' : 's'}
          </div>
        )}
      </div>

      {overlay.viewing && (
        <AuditAlertModal
          alert={overlay.viewing}
          saving={saving}
          onClose={overlay.closeOverlay}
          onDismiss={(a) => setStatus(a, 'Dismissed')}
          onEscalate={(a) => setStatus(a, 'Investigating')}
          onResolve={(a) => setStatus(a, 'Resolved')}
        />
      )}
    </div>
  );
}
